import React from 'react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button } from 'reactstrap';

import DroppableArea from '~components/drag-and-drop/DroppableArea';

import { FormField, MergeFieldRegistryEntry } from '~forms/types';
import { selectSelectedFieldId } from '~forms/selectors/formBuilder';
import { useAppSelector } from 'hooks';
import FieldCard from './FieldCard';

export const CANVAS_DROPPABLE_ID = 'form-canvas-drop';
export const CANVAS_BOTTOM_DROP_ID = 'form-canvas-bottom-drop';

interface ComponentProps {
  fields: FormField[];
  readOnly: boolean;
  registry: MergeFieldRegistryEntry[];
  onCreateVersion?: () => void;
}

const FormCanvas: React.FC<ComponentProps> = ({ fields, readOnly, registry, onCreateVersion }) => {
  const selectedFieldId = useAppSelector(selectSelectedFieldId);

  return (
    <DroppableArea id={CANVAS_DROPPABLE_ID} className="form-canvas">
      {readOnly && (
        <div className="form-canvas-readonly-overlay" role="status">
          <span>This version is published and read-only.</span>
          {onCreateVersion && (
            <Button color="success" size="sm" onClick={onCreateVersion}>
              Create New Version
            </Button>
          )}
        </div>
      )}

      {fields.length === 0 && !readOnly && (
        <div className="form-canvas-empty" aria-label="Empty canvas - drop fields here">
          <span className="form-canvas-empty-icon">+</span>
          <span>Drag fields here or click a field type in the palette</span>
        </div>
      )}

      <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy} disabled={readOnly}>
        {fields.map((field) => (
          <FieldCard
            key={field.id}
            field={field}
            isSelected={field.id === selectedFieldId}
            readOnly={readOnly}
            registry={registry}
          />
        ))}
      </SortableContext>

      {!readOnly && (
        <DroppableArea id={CANVAS_BOTTOM_DROP_ID} className="form-canvas-drop-zone" aria-label="Drop zone - add field at bottom">
          <span>Drop a field here to add it to the form</span>
        </DroppableArea>
      )}
    </DroppableArea>
  );
};

export default FormCanvas;
